import axios from 'axios';
import compact from 'lodash/compact';
import queryString from 'query-string';

import {
  CreateIPCameraDto,
  FetchIPCameraPhotosParams,
  FetchIPCamerasParams,
  FetchLiveStreamUrlParams,
  FetchPlaybackStreamUrlParams,
  StreamUrlResponse
} from 'types/dto/ip-camera.dto';
import IPCamera, {
  IPCameraCapabilities,
  IPCameraCapabilitiesWithChannels,
  IPCameraEditableFields,
  IPCameraEvent,
  IPCameraMetadata,
  IPCameraPTZPresetEditableFields,
  IPCameraPhoto,
  IPCameraPreview,
  PTZControl,
  PTZPreset
} from 'types/models/ip-camera';
import { APIResponseParsed, PaginatedResponse } from 'types/response';
import { deleteRequest, getRequest, patchRequest, postRequest } from 'utils/HttpRequest/http-request-utils';

import { normalizeChannelId } from './utils/normalize-ip-camera-channel-id';
import { parseAxiosError } from './utils/parse-axios-error';

async function fetchIPCameras(
  params: FetchIPCamerasParams
): Promise<APIResponseParsed<IPCamera[]>> {
  try {
    const response = await getRequest<IPCamera[]>('ip-camera', { params });
    return { data: response.data, error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

async function fetchIPCameraById(id: number): Promise<APIResponseParsed<IPCamera>> {
  const path = `ip-camera/${id}`;
  try {
    const response = await getRequest<IPCamera>(path);
    return { data: response.data, error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

async function fetchIPCameraPhotos(
  ipCameraId: number,
  params: FetchIPCameraPhotosParams
): Promise<APIResponseParsed<PaginatedResponse<IPCameraPhoto>>> {
  const path = `ip-cameras/${ipCameraId}/images`;
  try {
    const response = await getRequest<PaginatedResponse<IPCameraPhoto>>(path, { params });
    return { data: response.data, error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

async function fetchIPCameraONVIFStateById(id: number): Promise<APIResponseParsed<IPCameraMetadata>> {
  const path = `ip-camera/${id}/onvif-state`;
  try {
    const response = await getRequest<IPCameraMetadata>(path);
    return { data: response.data, error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

async function fetchIPCameraCapabilities(
  id: number,
  fields?: (keyof IPCameraCapabilities)[]
): Promise<APIResponseParsed<IPCameraCapabilitiesWithChannels>> {
  const path = `ip-camera/${id}/capabilities`;
  try {
    const response = await getRequest<IPCameraCapabilitiesWithChannels>(path, { params: { fields } });
    const { channels: rawChannels, ...partialCapabilities } = response.data;
    const data: IPCameraCapabilitiesWithChannels =
      rawChannels == null
        ? response.data
        : { ...partialCapabilities, channels: compact(rawChannels.map(normalizeChannelId)) };
    return { data, error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

async function takeIPCameraPreview(id: number): Promise<APIResponseParsed<IPCameraPreview>> {
  const path = `ip-camera/${id}/take-preview`;
  try {
    const response = await postRequest<IPCameraPreview, undefined>(path, undefined);
    return { data: response.data, error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

async function sendIPCameraControl(id: number, control: PTZControl): Promise<APIResponseParsed<'OK'>> {
  const path = `ip-camera/${id}/control`;
  try {
    await postRequest<unknown, PTZControl>(path, control);
    return { data: 'OK', error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
};

async function fetchIPCameraEvents(
  ipCameraId: number,
  startDate: string,
  endDate: string,
  channelId?: number
): Promise<APIResponseParsed<IPCameraEvent[]>> {
  const queryParams = queryString.stringify({
    startDate,
    endDate,
    channelId
  });

  const path = `ip-camera/${ipCameraId}/events?${queryParams}`;

  try {
    const response = await getRequest<IPCameraEvent[]>(path);
    return { data: response.data, error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

async function editIPCamera(id: number, values: IPCameraEditableFields): Promise<APIResponseParsed<IPCamera>> {
  const path = `ip-camera/${id}`;
  try {
    const response = await patchRequest<IPCamera, IPCameraEditableFields>(path, values);
    return { data: response.data, error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

async function deleteIPCamera(id: number): Promise<APIResponseParsed<'OK'>> {
  const path = `ip-camera/${id}`;
  try {
    await deleteRequest<unknown>(path);
    return { data: 'OK', error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

async function takeIPCameraSnapshot(ipCameraId: number): Promise<APIResponseParsed<IPCameraPhoto>> {
  const path = `ip-camera/${ipCameraId}/take-snapshot`;
  try {
    const response = await postRequest<IPCameraPhoto, undefined>(path, undefined);
    return { data: response.data, error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

async function fetchIPCameraPTZPresets(
  ipCameraId: number
): Promise<APIResponseParsed<PTZPreset[]>> {
  try {
    const response = await getRequest<PTZPreset[]>(`ip-camera/${ipCameraId}/v2/presets`);
    return { data: response.data, error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

async function createIPCameraPTZPreset(ipCameraId: number): Promise<APIResponseParsed<PTZPreset>> {
  const path = `ip-camera/${ipCameraId}/v2/presets/create`;
  try {
    const response = await postRequest<PTZPreset, undefined>(path, undefined);
    return { data: response.data, error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

async function updateIPCameraPTZPreset(
  ipCameraId: number,
  presetId: number,
  values: IPCameraPTZPresetEditableFields
): Promise<APIResponseParsed<PTZPreset>> {
  const path = `ip-camera/${ipCameraId}/v2/presets/${presetId}`;
  try {
    const response = await patchRequest<PTZPreset, IPCameraPTZPresetEditableFields>(path, values);
    return { data: response.data, error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

async function deleteIPCameraPTZPreset(ipCameraId: number, presetId: number): Promise<APIResponseParsed<'OK'>> {
  const path = `ip-camera/${ipCameraId}/v2/presets/${presetId}`;
  try {
    await deleteRequest<unknown>(path);
    return { data: 'OK', error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

async function activateIPCameraPTZPreset(ipCameraId: number, presetId: number): Promise<APIResponseParsed<'OK'>> {
  const path = `ip-camera/${ipCameraId}/v2/presets/${presetId}/activate`;
  try {
    await postRequest<unknown, undefined>(path, undefined);
    return { data: 'OK', error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

async function fetchLiveStreamUrl(
  id: number,
  params: FetchLiveStreamUrlParams
): Promise<APIResponseParsed<StreamUrlResponse>> {
  const path = `ip-camera/${id}/live-stream-url`;
  try {
    const response = await getRequest<StreamUrlResponse>(path, { params });
    return { data: response.data, error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

async function fetchPlaybackStreamUrl(
  id: number,
  params: FetchPlaybackStreamUrlParams
): Promise<APIResponseParsed<StreamUrlResponse>> {
  const path = `ip-camera/${id}/playback-stream-url`;
  try {
    const response = await getRequest<StreamUrlResponse>(path, { params });
    return { data: response.data, error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

async function createIPCamera(newIPCamera: CreateIPCameraDto): Promise<APIResponseParsed<IPCamera>> {
  const path = 'ip-camera';
  try {
    const response = await postRequest<IPCamera, CreateIPCameraDto>(path, newIPCamera);
    return { data: response.data, error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

// Grouping these functions for a namespace export, rather than named exports,
// to avoid naming collisions with action creators
export default {
  activateIPCameraPTZPreset,
  createIPCamera,
  createIPCameraPTZPreset,
  deleteIPCamera,
  deleteIPCameraPTZPreset,
  editIPCamera,
  fetchIPCameraById,
  fetchIPCameraCapabilities,
  fetchIPCameraEvents,
  fetchIPCameraONVIFStateById,
  fetchIPCameraPTZPresets,
  fetchIPCameraPhotos,
  fetchIPCameras,
  fetchLiveStreamUrl,
  fetchPlaybackStreamUrl,
  sendIPCameraControl,
  takeIPCameraPreview,
  takeIPCameraSnapshot,
  updateIPCameraPTZPreset
};